import { Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { LucideAngularModule } from "lucide-angular";
import { Router } from "@angular/router";
import { Court } from "../../model/court";
import { RouterService } from "../../services/router.service";
import { Gender, User } from "../../model/user";
import { CourtReservation } from "../../model/court-reservation";
import { CourtService } from "../../services/court.service";
import { CourtReservationService } from "../../services/court-reservation.service";
import { AuthenticationService } from "../../services/authentication.service";
import { PermissionEnum } from "../../model/permission-enum";
import { ClubService } from "../../services/club.service";
import { BookACourtComponent } from "../book-a-court/book-a-court.component";

@Component({
  selector: "app-home",
  standalone: true,
  imports: [CommonModule, BookACourtComponent, LucideAngularModule],
  templateUrl: "./home.component.html",
  styleUrl: "./home.component.css",
})
export class HomeComponent implements OnInit {
  constructor(
    private routerService: RouterService,
    private courtSvc: CourtService,
    private courtReservationService: CourtReservationService,
    private authService: AuthenticationService,
    private clubService: ClubService,
    private router: Router,
  ) {}
  courts: Court[] = [];
  upcomingMatch?: CourtReservation = undefined;
  courtReservations: CourtReservation[] = [];
  upcomingReservations: CourtReservation[] = [];
  reservationIndex = 0;
  currentUser?: User;
  hasAdminPermission = false;
  clubs: any[] = [];
  upcomingMatchClubName = "";
  upcomingMatchCourtName = "";
  ngOnInit(): void {
    this.currentUser = this.authService.currentUserValue || undefined;
    const permissions = this.authService.currentUserPermissions;
    this.hasAdminPermission = permissions.some(
      (permission: PermissionEnum) => permission == PermissionEnum.Admin,
    );

    if (this.hasAdminPermission) {
      this.router.navigate(["/employee-interface"]);
      return;
    }

    this.loadItems();
    this.loadClubs();
  }

  loadItems() {
    this.courtSvc.getItems().subscribe((items: Court[]) => {
      this.courts = items;
    });
  }

  loadClubs() {
    this.clubService.getClubs().subscribe({
      next: (clubs: any[]) => {
        this.clubs = clubs;
        this.loadReservations();
      },
      error: () => {
        this.loadReservations();
      },
    });
  }

  loadReservations() {
    if (!this.currentUser) return;
    const startDate = new Date(2000, 0, 1);
    const endDate = new Date(2100, 0, 1);
    this.courtReservationService
      .getCourtReservationsForUser(this.currentUser.id, startDate, endDate)
      .subscribe((reservations: any[]) => {
        this.courtReservations = reservations.map((r: any) => {
          const start = new Date(r.reservation_start_time);
          const players = (r.courtReservationPlayer || []).sort(
            (a: any, b: any) => a.position - b.position,
          );
          return {
            ...r,
            date: start.toISOString().split("T")[0],
            time: start.getHours(),
            player_ids: players.map((p: any) => p.player_id),
            courtReservationPlayer: players,
          } as CourtReservation;
        });
        this.upcomingReservations = this.getUpcomingReservations();
        if (this.upcomingReservations.length) {
          this.reservationIndex = 0;
          this.upcomingMatch = this.upcomingReservations[0];
          this.populateMatchDetails(this.upcomingMatch);
        } else {
          this.upcomingMatch = undefined;
        }
      });
  }

  goToBookACourt(id: number) {
    this.routerService.navigateTo("book-a-court/" + id);
  }
  getUpcomingMatch(): CourtReservation | undefined {
    let courtReservation: CourtReservation | undefined = undefined;
    let minDate = Infinity;
    const nowDate = new Date();

    for (let reservation of this.courtReservations) {
      if (reservation.finished) continue;
      const reservationDate = new Date(reservation.reservation_start_time);
      if (reservationDate < nowDate) continue;

      const players = reservation.player_ids || [];
      if (players.includes(this.currentUser!.id)) {
        const time = reservationDate.getTime();
        if (time < minDate) {
          minDate = time;
          courtReservation = reservation;
        }
      }
    }

    return courtReservation;
  }

  getUpcomingReservations(): CourtReservation[] {
    if (!this.currentUser) return [];
    const nowDate = new Date();
    return this.courtReservations
      .filter((reservation) => {
        if (reservation.finished) return false;
        const reservationDate = new Date(reservation.reservation_start_time);
        if (reservationDate < nowDate) return false;
        const players = reservation.player_ids || [];
        return players.includes(this.currentUser!.id);
      })
      .sort(
        (a, b) =>
          new Date(a.reservation_start_time).getTime() -
          new Date(b.reservation_start_time).getTime(),
      );
  }

  populateMatchDetails(match: CourtReservation) {
    const club = this.clubs.find((c) => c.id === (match as any).club_id);
    if (club) {
      this.upcomingMatchClubName = club.name;
    } else {
      this.upcomingMatchClubName = String((match as any).club_id);
    }
    this.courtSvc.getItem(match.court_id).subscribe((c: any) => {
      this.upcomingMatchCourtName = c.name;
    });
  }

  getUpcomingMatchDate(): string {
    const d = new Date(this.upcomingMatch!.reservation_start_time);
    const year = d.getFullYear();
    const month = (d.getMonth() + 1).toString().padStart(2, "0");
    const day = d.getDate().toString().padStart(2, "0");
    return `${day}.${month}.${year}`;
  }

  getUpcomingMatchCourt(): string {
    if (this.upcomingMatchCourtName) return this.upcomingMatchCourtName;
    let court = this.courts.find(
      (court) => this.upcomingMatch!.court_id == court.id,
    );
    return court ? court.name : String(this.upcomingMatch!.court_id);
  }

  playerName(i: number): string {
    const player = this.upcomingMatch?.courtReservationPlayer?.[i];
    if (!player) return "";
    if (player.player_id == this.currentUser!.id) return "Me";
    return player.player?.full_name || "";
  }

  getUpcomingMatchTime(): string {
    const d = new Date(this.upcomingMatch!.reservation_start_time);
    const h = d.getHours().toString().padStart(2, "0");
    const m = d.getMinutes().toString().padStart(2, "0");
    return `${h}:${m}`;
  }

  getClubName(): string {
    return this.upcomingMatchClubName;
  }

  goToUnimplemented() {
    this.routerService.navigateTo("unimplemented");
  }

  goToApproveNewUsers() {
    this.routerService.navigateTo("newRegistrations");
  }

  goToApproveBookings() {
    this.routerService.navigateTo("system-alerts");
  }

  goToEmployeeInterface() {
    this.routerService.navigateTo("employee-interface");
  }

  getUpcomingMatchTimeString() {
    const now = new Date();
    const matchDate = new Date(this.upcomingMatch!.reservation_start_time);
    let diff = matchDate.getTime() - now.getTime();
    if (diff <= 0) return "Your match starts soon!";
    const minutes = Math.floor(diff / 60000);
    if (minutes < 60) return `Your match starts in ${minutes} minutes`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `Your match starts in ${hours} hours`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `Your match starts in ${days} days`;
    const months = Math.floor(days / 30);
    return `Your match starts in ${months} months`;
  }

  startMatch() {
    if (!this.upcomingMatch) return;
    this.courtReservationService
      .updateCourtReservation({
        ...this.upcomingMatch,
        started: true,
        admin_approve_start: false,
      })
      .subscribe(() => {
        if (this.upcomingMatch) {
          this.upcomingMatch.started = true;
        }
        this.goToYourMatch();
      });
  }

  goToYourMatch() {
    if (!this.upcomingMatch) return;
    this.routerService.navigateTo(`your-match/${this.upcomingMatch.id}`);
  }

  prevReservation() {
    if (this.upcomingReservations.length <= 1) return;
    this.reservationIndex =
      (this.reservationIndex - 1 + this.upcomingReservations.length) %
      this.upcomingReservations.length;
    this.upcomingMatch = this.upcomingReservations[this.reservationIndex];
    this.populateMatchDetails(this.upcomingMatch);
  }

  nextReservation() {
    if (this.upcomingReservations.length <= 1) return;
    this.reservationIndex =
      (this.reservationIndex + 1) % this.upcomingReservations.length;
    this.upcomingMatch = this.upcomingReservations[this.reservationIndex];
    this.populateMatchDetails(this.upcomingMatch);
  }

  canCancel(res: CourtReservation): boolean {
    const now = new Date();
    const start = new Date(res.reservation_start_time);
    return start.getTime() - now.getTime() >= 8 * 60 * 60 * 1000;
  }

  cancelCurrentReservation() {
    const res = this.upcomingMatch;
    if (!res || !res.id || !this.canCancel(res)) return;
    this.courtReservationService
      .deleteCourtReservation(res.id)
      .subscribe(() => {
        this.upcomingReservations = this.upcomingReservations.filter(
          (r) => r.id !== res.id,
        );
        this.courtReservations = this.courtReservations.filter(
          (r) => r.id !== res.id,
        );
        if (this.upcomingReservations.length === 0) {
          this.upcomingMatch = undefined;
        } else {
          this.reservationIndex =
            this.reservationIndex % this.upcomingReservations.length;
          this.upcomingMatch = this.upcomingReservations[this.reservationIndex];
          this.populateMatchDetails(this.upcomingMatch);
        }
      });
  }
}
